/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCheck,
  FiCalendar,
  FiGlobe,
  FiUser,
} from 'react-icons/fi';
import {
  MdOutlineAccountBalanceWallet,
  MdOutlineCreditCard,
  MdOutlineQrCode2,
} from 'react-icons/md';
import { IoIosCard } from 'react-icons/io';
import { PiBankBold } from 'react-icons/pi';
import AppAlert from '../../../../shared/components/app-alert/AppAlert';
import { countryMobileCodes } from '../../../../shared/constants/CountryOptions';
import EsimTravelerForm from '../../components/EsimTravelerForm/EsimTravelerForm';
import EsimDocumentUpload from '../../components/EsimDocumentUpload/EsimDocumentUpload';
import EsimFareSummary from '../../components/EsimFareSummary/EsimFareSummary';
import EsimReviewModal from '../../components/EsimReviewModal/EsimReviewModal';
import { performEsimBookingApi } from '../../api/esimApi';
import {
  ESIM_ROUTES,
  ESIM_DOCUMENT_FIELDS,
} from '../../constants/esimConstants';
import {
  isValidEmail,
  isValidMobileNumber,
  isValidPassportNumber,
  toEsimDateString,
  tripDurationInDays,
  derivePlanTitle,
} from '../../utils/esimUtils';
import './EsimBookingPage.scss';
import AppIconButton from '../../../../shared/components/app-icon-button/AppIconButton';
import AppPaymentGatewaySelector from '../../../../shared/components/app-payment-gateway-selector/AppPaymentGatewaySelector';

const buildInitialTraveler = (search) => ({
  title: '',
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  age: '',
  passportNumber: '',
  passportExpiry: '',
  nationality: search?.nationality || 'IN',
  email: '',
  mobile: '',
  mobileCountryCode: 'IN',
});

const buildInitialDocs = () => {
  const seed = {};
  ESIM_DOCUMENT_FIELDS.forEach((f) => {
    seed[f.key] = { documentUrl: '', documentFormat: '', fileName: '' };
  });
  return seed;
};

function EsimBookingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state || {};
  const plan = navState.plan || null;
  const search = navState.search || null;

  // Bounce back to landing if state is missing.
  useEffect(() => {
    if (!plan || !search?.country) {
      navigate(ESIM_ROUTES.LANDING, { replace: true });
    }
  }, [plan, search, navigate]);

  const [traveler, setTraveler] = useState(() => buildInitialTraveler(search));
  const [docs, setDocs] = useState(() => buildInitialDocs());
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [showErrors, setShowErrors] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [paymentMode, setPaymentMode] = useState('cashcard');
  const [selectedPg, setSelectedPg] = useState('');
  const [alertMsg, setAlertMsg] = useState(null);

  const nationalityOptions = useMemo(
    () =>
      countryMobileCodes.map((c) => ({
        value: c.code,
        label: c.name,
      })),
    [],
  );

  const travelerValid = useMemo(() => {
    if (!traveler) return false;
    return (
      !!traveler.title &&
      !!traveler.firstName?.trim() &&
      !!traveler.lastName?.trim() &&
      !!traveler.dateOfBirth &&
      !!traveler.passportExpiry &&
      !!traveler.nationality &&
      isValidPassportNumber(traveler.passportNumber || '') &&
      isValidEmail(traveler.email) &&
      isValidMobileNumber(
        traveler.mobile || '',
        traveler.mobileCountryCode || 'IN',
      )
    );
  }, [traveler]);

  const docsValid = useMemo(() => {
    return ESIM_DOCUMENT_FIELDS.filter((f) => f.required).every(
      (f) => !!docs?.[f.key]?.documentUrl,
    );
  }, [docs]);

  const tripDays = tripDurationInDays(search?.travelStart, search?.travelEnd);

  const handlePayClick = () => {
    setShowErrors(true);
    if (!travelerValid) {
      setAlertMsg({
        variant: 'error',
        description:
          'Please complete the traveller details and ensure passport, email & mobile are valid.',
      });
      return;
    }
    if (!docsValid) {
      setAlertMsg({
        variant: 'error',
        description: 'Please upload all the required documents',
      });
      return;
    }
    if (!termsAccepted) {
      setAlertMsg({
        variant: 'error',
        description: 'Please accept the terms and conditions to continue.',
      });
      return;
    }
    setBookingError('');
    setReviewOpen(true);
  };

  const buildBookingPayload = () => {
    const payload = {
      cachekey: plan?.cachekey || '',
      paxnamefirstname: (traveler.firstName || '').trim(),
      paxnamelastname: (traveler.lastName || '').trim(),
      paxtitle: traveler.title,
      paxdob: toEsimDateString(traveler.dateOfBirth),
      paxpassportno: (traveler.passportNumber || '').trim().toUpperCase(),
      paxnationality: traveler.nationality || 'IN',
      paxemail: (traveler.email || '').trim(),
      paxmobile: (traveler.mobile || '').trim(),
      travelstartdate: toEsimDateString(search?.travelStart),
      travelenddate: toEsimDateString(search?.travelEnd),
      planid: plan?.planId || '',
      planname: plan?.planName || '',
      paymentmode: paymentMode,
      pgname: paymentMode === 'cashcard' ? '' : selectedPg,
    };
    ESIM_DOCUMENT_FIELDS.forEach((f) => {
      payload[f.key] = docs?.[f.key]?.documentUrl || '';
    });
    return payload;
  };

  const handleConfirmBooking = async () => {
    setSubmitting(true);
    setBookingError('');
    try {
      const payload = buildBookingPayload();
      const result = await performEsimBookingApi(payload);
      if (result && result.ok) {
        if (paymentMode === 'cashcard') {
          setReviewOpen(false);
          navigate(
            ESIM_ROUTES.VOUCHER.replace(
              ':transactionId',
              encodeURIComponent(result.transactionId),
            ),
            {
              state: {
                plan,
                search,
                traveler,
                docs,
                transactionId: result.transactionId,
                message: result.message,
              },
            },
          );
        } else {
          const { response } = result;
          const apiResult = response?.ApiStatus?.Result || {};

          if (apiResult?.PgUrl && response?.ApiStatus?.TransactionStatus) {
            window.location.href = `${apiResult.PgUrl}?txid=${apiResult.Txid}`;
          } else {
            setAlertMsg({
              variant: 'error',
              description:
                result?.message ||
                'Failed to book, please contact customer care',
            });
          }
        }
      } else {
        setAlertMsg({
          variant: 'error',
          description:
            result.message || 'Failed to book, please contact customer care',
        });
      }
      setBookingError(
        result.message ||
          'eSIM booking failed. Please review the details and retry.',
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setBookingError('eSIM booking failed. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!plan || !search?.country) return null;

  return (
    <main className="esim-booking-page">
      {alertMsg && (
        <AppAlert
          showIcon
          variant={alertMsg.variant}
          description={alertMsg.description}
          onHide={() => setAlertMsg(null)}
        />
      )}

      <section className="esim-booking-page__summary-bar">
        <Container>
          <div className="esim-booking-page__summary-inner">
            <div className="esim-booking-page__summary-meta">
              <span className="esim-booking-page__summary-label">
                <FiGlobe /> Destination
              </span>
              <strong>{search.country || '—'}</strong>
            </div>
            <div className="esim-booking-page__summary-meta">
              <span className="esim-booking-page__summary-label">
                <FiCalendar /> Travel
              </span>
              <strong>
                {search.travelStart || '—'}
                <span aria-hidden> → </span>
                {search.travelEnd || '—'}
                {tripDays > 0 && (
                  <small>
                    {' '}
                    ({tripDays} {tripDays === 1 ? 'day' : 'days'})
                  </small>
                )}
              </strong>
            </div>
            <div className="esim-booking-page__summary-meta">
              <span className="esim-booking-page__summary-label">
                <FiUser /> Plan
              </span>
              <strong>{derivePlanTitle(plan)}</strong>
            </div>
          </div>
        </Container>
      </section>

      <Container className="esim-booking-page__main">
        <Row className="g-4">
          <Col lg={8} md={12}>
            <EsimTravelerForm
              traveler={traveler}
              countries={nationalityOptions}
              onChange={setTraveler}
              showErrors={showErrors}
              travelStartDate={search?.travelStart}
            />
            <EsimDocumentUpload
              docs={docs}
              onChange={setDocs}
              showErrors={showErrors}
            />
            <div className="online-visa-booking__payment">
              <h5 className="online-visa-booking__payment-title">
                Payment Method
              </h5>
              <div className="online-visa-booking__payment-modes gap-5">
                <AppIconButton
                  onClick={() => setPaymentMode('cashcard')}
                  variant="transparent"
                  className={`pg-btn  p-0 ${
                    paymentMode === 'cashcard' ? 'red-color' : 'grey-color'
                  }`}
                >
                  <div>
                    <MdOutlineAccountBalanceWallet />
                    <span>Wallet</span>
                  </div>
                </AppIconButton>
                <AppIconButton
                  onClick={() => setPaymentMode('creditcard')}
                  variant="transparent"
                  className={`pg-btn  p-0 ${
                    paymentMode === 'creditcard' ? 'red-color' : 'grey-color'
                  }`}
                >
                  <div>
                    <MdOutlineCreditCard />
                    <span>Credit Card</span>
                  </div>
                </AppIconButton>
                <AppIconButton
                  onClick={() => setPaymentMode('debitcard')}
                  variant="transparent"
                  className={`pg-btn  p-0 ${
                    paymentMode === 'debitcard' ? 'red-color' : 'grey-color'
                  }`}
                >
                  <div>
                    <IoIosCard />
                    <span>Debit Card</span>
                  </div>
                </AppIconButton>
                <AppIconButton
                  onClick={() => setPaymentMode('netbanking')}
                  variant="transparent"
                  className={`pg-btn  p-0 ${
                    paymentMode === 'netbanking' ? 'red-color' : 'grey-color'
                  }`}
                >
                  <div>
                    <PiBankBold />
                    <span>Net Banking</span>
                  </div>
                </AppIconButton>
                <AppIconButton
                  onClick={() => setPaymentMode('upi')}
                  variant="transparent"
                  className={`pg-btn  p-0 ${
                    paymentMode === 'upi' ? 'red-color' : 'grey-color'
                  }`}
                >
                  <div>
                    <MdOutlineQrCode2 />
                    <span>UPI</span>
                  </div>
                </AppIconButton>
              </div>
              {paymentMode !== 'cashcard' && (
                <AppPaymentGatewaySelector
                  paymentModeClicked={paymentMode}
                  selectedPgForPaymentMode={selectedPg}
                  onPgSelect={setSelectedPg}
                />
              )}
            </div>
          </Col>

          <Col lg={4} md={12}>
            <div className="esim-booking-page__sidebar">
              <EsimFareSummary plan={plan} currency={plan?.currency} />
            </div>
          </Col>
        </Row>
      </Container>

      <div className="esim-booking-page__bottom-bar">
        <Container className="esim-booking-page__bottom-inner">
          <label
            className="esim-booking-page__terms"
            htmlFor="esim-terms-checkbox"
          >
            <input
              id="esim-terms-checkbox"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            />
            <span>
              I confirm the details and agree to the{' '}
              <a
                href="https://agents.fastays.com/terms-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                terms and conditions
              </a>
              .
            </span>
          </label>
          <div className="esim-booking-page__bottom-actions">
            <button
              type="button"
              className="esim-booking-page__back-btn"
              onClick={() => navigate(-1)}
            >
              <FiArrowLeft />
              <span>Back to Plans</span>
            </button>
            <button
              type="button"
              className="esim-booking-page__pay-btn"
              onClick={handlePayClick}
              disabled={submitting}
            >
              <FiCheck />
              <span>Review &amp; Pay</span>
            </button>
          </div>
        </Container>
      </div>

      <EsimReviewModal
        show={reviewOpen}
        plan={plan}
        traveler={traveler}
        search={search}
        docs={docs}
        submitting={submitting}
        errorMsg={bookingError}
        onConfirm={handleConfirmBooking}
        onHide={() => {
          if (submitting) return;
          setReviewOpen(false);
          setBookingError('');
        }}
      />
    </main>
  );
}

export default EsimBookingPage;
